import { v4 as uuidv4 } from "uuid";
import React, { useState } from "react";
import { Link, withRouter } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Confirm,
  Dropdown,
  Icon,
  Input,
  Loader,
  Table,
  Header,
  Form,
  List,
  Label,
  Modal,
  Popup,
  Search,
} from "semantic-ui-react";
import {
  RESET_PASSWORD,
  RESEND_INVITATION,
  GET_USER,
  ADMIN_CHANGE_USER_EMAIL,
  ADMIN_GET_STUDENT,
  ADMIN_PUT_USER,
  ADMIN_DELETE_USER,
  ADMIN_TRANSFER_STUDENTS,
} from "../../GraphQL";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import UserSearch from "./UserSearch";

const ItemLabel = ({ label, removable, onRemove, ...props }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <>
      <Label as="a" {...props} onClick={() => setConfirmOpen(true)}>
        {label} {removable ? <Icon name="delete" /> : null}
      </Label>
      <Confirm
        open={confirmOpen}
        content={`Are you sure you want to remove ${label}?`}
        confirmButton="Remove"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onRemove}
      />
    </>
  );
};

const CognitoUserList = ({ user }) => (
  <List>
    {Object.keys(user)
      .filter((k) => k !== "__typename")
      .map((k) => (
        <List.Item key={k}>
          <List.Header>{k}</List.Header>
          {user[k] || "Not found"}
        </List.Item>
      ))}
  </List>
);

const getAdminUserInput = (user) => {
  return {
    sub: user.sub,
    groups: user.groups,
    assistants: user.assistants.map((a) => a.sub),
    teachers: user.teachers.map((t) => t.sub),
    children: user.children.map((c) => c.id),
    ...(user.stripeCustomerId
      ? {
          stripeCustomerId: user.stripeCustomerId,
        }
      : {}),
    ...(user.subscription_id
      ? {
          subscription_id: user.subscription_id,
        }
      : {}),
  };
};

const GroupDropdown = ({ value, onChange }) => {
  const options = [
    { key: 1, text: "Teachers", value: "Teachers" },
    { key: 2, text: "Parents", value: "Parents" },
    { key: 3, text: "Assistants", value: "Assistants" },
    { key: 4, text: "Administrators", value: "Administrators" },
  ];
  return (
    <Dropdown
      fluid
      selection
      multiple
      value={value}
      options={options}
      placeholder="Add Groups"
      onChange={onChange}
    />
  );
};

const StudentSearch = ({ onSelect }) => {
  const [getUser, { loading, data }] = useLazyQuery(ADMIN_GET_STUDENT);
  const debouncedGetUser = debounce((_e, { value }) => {
    getUser({
      variables: { id: value },
      fetchPolicy: "network-only",
    });
  }, 1000);

  return (
    <Search
      loading={loading}
      icon="add"
      placeholder={uuidv4()}
      onSearchChange={debouncedGetUser}
      onResultSelect={() => onSelect(data.getStudent)}
      results={
        data && data.getStudent
          ? [
              {
                title: data.getStudent.name,
                id: data.getStudent.id,
              },
            ]
          : []
      }
    />
  );
};

const ResetPasswordButton = ({ user, ...props }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [password, setPassword] = useState("ChangeMe1");
  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

  return (
    <Modal
      onClose={() => setModalIsOpen(false)}
      onOpen={() => setModalIsOpen(true)}
      open={modalIsOpen}
      trigger={<Button {...props} />}
    >
      <Modal.Header>Reset Password</Modal.Header>
      <Modal.Content>
        <p>
          Reset the password for <b>{user.name}</b>?
        </p>
        <label className="block">
          Temporary Password
          <Form.Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={() => setModalIsOpen(false)}>
          Cancel
        </Button>
        <Button
          loading={loading}
          content="Reset"
          onClick={() =>
            resetPassword({
              variables: {
                email: user.email,
                password: password,
              },
              onCompleted: () => setModalIsOpen(false),
            })
          }
          positive
        />
      </Modal.Actions>
    </Modal>
  );
};

const ChangeEmailButton = ({ user, ...props }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [adminChangeUserEmail, { loading }] = useMutation(
    ADMIN_CHANGE_USER_EMAIL,
  );

  return (
    <Modal
      onClose={() => setModalIsOpen(false)}
      onOpen={() => setModalIsOpen(true)}
      open={modalIsOpen}
      trigger={<Button {...props} />}
    >
      <Modal.Header>Change user email</Modal.Header>
      <Modal.Content>
        <p>
          Changing the user's email address will send a new invitation to the
          user at the new address.
        </p>
        <label className="block">
          New email address
          <Form.Input
            type="email"
            value={newEmailAddress}
            onChange={(e) => setNewEmailAddress(e.target.value)}
          />
        </label>
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={() => setModalIsOpen(false)}>
          Cancel
        </Button>
        <Button
          loading={loading}
          content="Change"
          onClick={() =>
            adminChangeUserEmail({
              variables: {
                newEmailAddress,
                userId: user.sub,
              },
              onCompleted: () => {
                setModalIsOpen(false);
                window.location.reload();
              },
            })
          }
          positive
        />
      </Modal.Actions>
    </Modal>
  );
};

const TransferStudentsButton = ({ user, ...props }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newTeacher, setNewTeacher] = useState();
  const [transferStudents, { loading }] = useMutation(ADMIN_TRANSFER_STUDENTS);

  return (
    <Modal
      onClose={() => setModalIsOpen(false)}
      onOpen={() => setModalIsOpen(true)}
      open={modalIsOpen}
      trigger={<Button {...props} />}
    >
      <Modal.Header>Transfer Students</Modal.Header>
      <Modal.Content>
        <p>Transfer all students to teacher:</p>
        <label className="block">
          Teacher
          <UserSearch
            initialUser={newTeacher}
            onSelect={(u) => setNewTeacher(u.sub)}
          />
        </label>
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={() => setModalIsOpen(false)}>
          Cancel
        </Button>
        <Button
          disabled={!newTeacher}
          loading={loading}
          content="Transfer"
          onClick={() =>
            transferStudents({
              variables: {
                currentUser: user.sub,
                newUser: newTeacher,
              },
              onCompleted: () => {
                setModalIsOpen(false);
                window.location.reload();
              },
            })
          }
          positive
        />
      </Modal.Actions>
    </Modal>
  );
};

const DeleteUserButton = ({ user, ...props }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteUser, { loading }] = useMutation(ADMIN_DELETE_USER);

  return (
    <>
      <Confirm
        open={confirmOpen}
        content={`Are you sure you want to delete ${user.name}?`}
        confirmButton={{ negative: true, children: "Delete", loading }}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await deleteUser({ variables: { id: user.sub } });
          window.location.reload();
        }}
      />
      <Button negative onClick={() => setConfirmOpen(true)} {...props} />
    </>
  );
};

const ResendInvitationButton = ({ user, ...props }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resendInvitation, { loading }] = useMutation(RESEND_INVITATION);

  return (
    <>
      <Confirm
        open={confirmOpen}
        content={`Are you sure you want to resend an invitation to ${user.name}?`}
        confirmButton={{ negative: false, children: "Resend", loading }}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await resendInvitation({ variables: { user: user.sub } });
          setConfirmOpen(false);
        }}
      />
      <Button onClick={() => setConfirmOpen(true)} {...props} />
    </>
  );
};

const UserTable = ({ initialUser, editing, onSubmit, onCancel }) => {
  const [user, setUser] = useState(initialUser);
  const confirmed = initialUser.cognitoUser?.status === "CONFIRMED";
  return (
    <Form>
      <Table definition>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Name</Table.Cell>
            <Table.Cell>{user.name}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Email</Table.Cell>
            <Table.Cell>
              {user.email}{" "}
              <ChangeEmailButton size="mini" user={user}>
                Change
              </ChangeEmailButton>
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>ID</Table.Cell>
            <Table.Cell>
              {user.sub}{" "}
              <Popup
                position="top center"
                content="Copied!"
                mouseLeaveDelay={500}
                on="click"
                trigger={
                  <Icon
                    onClick={() => navigator.clipboard.writeText(user.sub)}
                    name="copy"
                  />
                }
              />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell
              negative={
                !user.groups.includes("Teachers") && user.stripeCustomerId
              }
            >
              Stripe Customer
            </Table.Cell>
            <Table.Cell>
              {editing ? (
                <Input
                  placeholder="cus_XXX"
                  value={user.stripeCustomerId}
                  onChange={(e) =>
                    setUser({ ...user, stripeCustomerId: e.target.value })
                  }
                />
              ) : user.stripeCustomerId ? (
                <span>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`}
                  >
                    {user.stripeCustomerId} <Icon name="external" />
                  </a>
                </span>
              ) : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Subscription ID</Table.Cell>
            <Table.Cell>
              {editing ? (
                <Input
                  placeholder="sub_XXX"
                  value={user.subscription_id}
                  onChange={(e) =>
                    setUser({ ...user, subscription_id: e.target.value })
                  }
                />
              ) : user.subscription_id ? (
                <span>
                  <Link to={`/admin/subscriptions/${user.subscription_id}`}>
                    {user.subscription_id}
                  </Link>{" "}
                </span>
              ) : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell negative={user.groups.length !== 1}>Groups</Table.Cell>
            <Table.Cell>
              {editing ? (
                <GroupDropdown
                  value={user.groups}
                  onChange={(e, { value }) =>
                    setUser({
                      ...user,
                      groups: value,
                    })
                  }
                />
              ) : (
                <Label.Group>
                  {user.groups.map((g) => (
                    <ItemLabel size="medium" label={g} key={g} />
                  ))}
                </Label.Group>
              )}
            </Table.Cell>
          </Table.Row>
          {user.group && (
            <Table.Row>
              <Table.Cell>Invited Group</Table.Cell>
              <Table.Cell>{user.group}</Table.Cell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.Cell>Cognito User</Table.Cell>
            <Table.Cell>
              {user.cognitoUser ? (
                <Popup
                  trigger={
                    <Icon
                      circular
                      inverted
                      {...(confirmed
                        ? { color: "green", name: "check" }
                        : { color: "yellow", name: "exclamation" })}
                    />
                  }
                  content={<CognitoUserList user={user.cognitoUser} />}
                />
              ) : (
                "Not found"
              )}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell
              negative={
                !user.groups.includes("Parents") && user.children.length > 0
              }
            >
              Children
            </Table.Cell>
            <Table.Cell>
              <List>
                {user.children.map((c, i) => (
                  <List.Item key={c ? c.id : `child-${i}`}>
                    {editing && !user.group ? (
                      <ItemLabel
                        size="medium"
                        label={c ? c.name : "null"}
                        key={c ? c.id : `child-${i}`}
                        onRemove={() =>
                          setUser({
                            ...user,
                            children: user.children.filter(
                              (child) =>
                                child !== null && child.id !== (c ? c.id : c),
                            ),
                          })
                        }
                        removable
                      />
                    ) : (
                      <>
                        {c ? (
                          <Link to={`/admin/students/${c.id}`}>{c.name}</Link>
                        ) : (
                          "Child does not exist"
                        )}
                        {c && c.archived ? (
                          <Label basic size="mini" horizontal>
                            Archived
                          </Label>
                        ) : null}
                      </>
                    )}
                  </List.Item>
                ))}
                {editing && !user.group ? (
                  <StudentSearch
                    onSelect={(newStudent) => {
                      console.log(newStudent);
                      setUser({
                        ...user,
                        children: [...user.children, newStudent],
                      });
                    }}
                  />
                ) : null}
              </List>
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell
              negative={
                !user.groups.includes("Teachers") && user.students.length > 0
              }
            >
              Students
            </Table.Cell>
            <Table.Cell>
              <List>
                {user.students.map((c) => (
                  <List.Item key={c.id}>
                    <Link to={`/admin/students/${c.id}`}>{c.name}</Link>
                    {c.archived ? (
                      <Label basic size="mini" horizontal>
                        Archived
                      </Label>
                    ) : null}
                  </List.Item>
                ))}
              </List>
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Assistants</Table.Cell>
            <Table.Cell>
              {" "}
              <List>
                {user.assistants.map((a, i) => (
                  <List.Item key={a ? a.sub : `assistant-${i}`}>
                    {editing ? (
                      <ItemLabel
                        size="medium"
                        label={a ? a.name : "User not found"}
                        key={a ? a.sub : `assistant-label-${i}`}
                        onRemove={() =>
                          setUser({
                            ...user,
                            assistants: user.assistants.filter(
                              (u) => u !== null && u.sub !== (a ? a.sub : a),
                            ),
                          })
                        }
                        removable
                      />
                    ) : (
                      <>
                        {a ? (
                          <a href={`/admin/users/${a.sub}`}>{a.name}</a>
                        ) : (
                          "User not found"
                        )}
                      </>
                    )}
                  </List.Item>
                ))}
                {editing ? (
                  <UserSearch
                    onSelect={(newAssistant) => {
                      console.log(newAssistant);
                      setUser({
                        ...user,
                        assistants: [...user.assistants, newAssistant],
                      });
                    }}
                  />
                ) : null}
              </List>
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Teachers</Table.Cell>
            <Table.Cell>
              {" "}
              <List>
                {user.teachers.map((t) => (
                  <List.Item key={t.sub}>
                    {editing ? (
                      <ItemLabel
                        size="medium"
                        label={t.name}
                        key={t.id}
                        onRemove={() =>
                          setUser({
                            ...user,
                            teachers: user.teachers.filter(
                              (u) => u.sub !== t.sub,
                            ),
                          })
                        }
                        removable
                      />
                    ) : (
                      <a href={`/admin/users/${t.sub}`}>{t.name}</a>
                    )}
                  </List.Item>
                ))}
                {editing ? (
                  <UserSearch
                    onSelect={(newTeacher) => {
                      console.log(newTeacher);
                      setUser({
                        ...user,
                        teachers: [...user.teachers, newTeacher],
                      });
                    }}
                  />
                ) : null}
              </List>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      {editing ? (
        <>
          <Button
            floated="right"
            onClick={() => {
              setUser(initialUser);
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button floated="right" color="teal" onClick={() => onSubmit(user)}>
            Save
          </Button>
        </>
      ) : null}
    </Form>
  );
};

const UserAdminPage = ({ match }) => {
  const [editing, setEditing] = useState(false);
  const { loading, error, data } = useQuery(GET_USER, {
    variables: { id: match.params.id },
  });
  const [adminPutUser] = useMutation(ADMIN_PUT_USER);
  if (loading) {
    return <Loader active />;
  } else if (error) {
    throw error;
  }

  const user = data.user;

  if (!user) {
    return (
      <div>
        <Breadcrumb size="tiny">
          <Breadcrumb.Section>
            <Link to="/admin">Admin Home</Link>
          </Breadcrumb.Section>
          <Breadcrumb.Divider icon="right angle" />
          <Breadcrumb.Section active>{match.params.id}</Breadcrumb.Section>
        </Breadcrumb>
        <Header as="h1">User Not Found</Header>
      </div>
    );
  }

  const isInvitation = user.group && user.cognitoUser === null;

  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/admin">Admin Home</Link>
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>{user.name}</Breadcrumb.Section>
      </Breadcrumb>
      {isInvitation ? (
        <ResendInvitationButton user={user} floated="right">
          Resend Invitation
        </ResendInvitationButton>
      ) : null}
      <ResetPasswordButton user={user} floated="right">
        Reset Password
      </ResetPasswordButton>

      <Button
        floated="right"
        onClick={() => setEditing(true)}
        disabled={editing}
      >
        Edit
      </Button>
      {user.students && user.students.length > 0 ? (
        <TransferStudentsButton user={user} primary floated="right">
          Transfer Students
        </TransferStudentsButton>
      ) : null}
      <DeleteUserButton user={user} floated="right">
        Delete {isInvitation ? "Invitation" : "User"}
      </DeleteUserButton>
      <Header color={isInvitation ? "grey" : "black"} as="h1">
        {user.name}{" "}
        <Label color={user.isProUser ? "blue" : "grey"} horizontal>
          {user.isProUser ? "PRO" : isInvitation ? "Invitation" : "Free"}
        </Label>
      </Header>
      <UserTable
        initialUser={user}
        editing={editing}
        onSubmit={(newUser) => {
          setEditing(false);
          const params = getAdminUserInput(newUser);
          //alert(JSON.stringify(param, undefined, 2));
          adminPutUser({ variables: { input: params } });
        }}
        onCancel={() => setEditing(false)}
      />
    </div>
  );
};

export default withRouter(UserAdminPage);
